const geolocationModule =
	import.meta.env.VITE_CAPACITOR_BUILD === "true"
		? import("@capacitor/geolocation")
		: null;

export async function getCurrentPosition(
	options: PositionOptions,
): Promise<GeolocationPosition> {
	if (geolocationModule) {
		const { Geolocation } = await geolocationModule;
		const permissions = await Geolocation.checkPermissions().catch(
			(err: unknown) => {
				throw toGeolocationPositionError(err);
			},
		);
		if (permissions.location !== "granted") {
			const requestResult = await Geolocation.requestPermissions().catch(
				(err: unknown) => {
					throw toGeolocationPositionError(err);
				},
			);
			if (requestResult.location !== "granted") {
				throw {
					code: 1,
					message: "Location permission not granted",
					PERMISSION_DENIED: 1,
					POSITION_UNAVAILABLE: 2,
					TIMEOUT: 3,
				} as GeolocationPositionError;
			}
		}

		const position = await Geolocation.getCurrentPosition(options).catch(
			(err: unknown) => {
				throw toGeolocationPositionError(err);
			},
		);
		const coords = {
			latitude: position.coords.latitude,
			longitude: position.coords.longitude,
			accuracy: position.coords.accuracy,
			altitude: position.coords.altitude,
			speed: position.coords.speed,
			heading: position.coords.heading,
			altitudeAccuracy: position.coords.altitudeAccuracy || null,
		};
		return {
			coords: {
				...coords,
				toJSON: () => ({
					...coords,
				}),
			},
			timestamp: position.timestamp,
			toJSON: () => ({
				coords,
				timestamp: position.timestamp,
			}),
		} satisfies GeolocationPosition;
	}
	return new Promise<GeolocationPosition>((resolve, reject) => {
		if (!("geolocation" in navigator)) {
			reject({
				code: 2,
				message: "Geolocation not supported",
				PERMISSION_DENIED: 1,
				POSITION_UNAVAILABLE: 2,
				TIMEOUT: 3,
			});
			return;
		}
		navigator.geolocation.getCurrentPosition(resolve, reject, options);
	});
}

// Capacitor rejects with string codes such as "OS-PLUG-GLOC-0010". Callers
// branch on the numeric GeolocationPositionError codes used by the browser API,
// so native codes are mapped here. The original code is kept as nativeCode for
// diagnostics. Codes are defined in the plugin's GeolocationError.swift and
// GeolocationErrors.kt.
// See https://capacitorjs.com/docs/apis/geolocation#errors
// Location turned off by the user is mapped to PERMISSION_DENIED rather than
// POSITION_UNAVAILABLE because callers retry on POSITION_UNAVAILABLE, which
// would re-show the Android enable-location dialog the user just declined.
const PERMISSION_DENIED = 1;
const POSITION_UNAVAILABLE = 2;
const TIMEOUT = 3;
const NATIVE_CODE_TO_POSITION_ERROR_CODE: Record<string, number> = {
	"OS-PLUG-GLOC-0002": POSITION_UNAVAILABLE, // error obtaining location
	"OS-PLUG-GLOC-0003": PERMISSION_DENIED, // permission denied
	"OS-PLUG-GLOC-0004": POSITION_UNAVAILABLE, // invalid getCurrentPosition input (iOS)
	"OS-PLUG-GLOC-0005": POSITION_UNAVAILABLE, // invalid watchPosition input (iOS)
	"OS-PLUG-GLOC-0006": POSITION_UNAVAILABLE, // invalid clearWatch input (iOS)
	"OS-PLUG-GLOC-0007": PERMISSION_DENIED, // location services not enabled
	"OS-PLUG-GLOC-0008": PERMISSION_DENIED, // permission restricted (iOS)
	"OS-PLUG-GLOC-0009": PERMISSION_DENIED, // enable location request denied (Android)
	"OS-PLUG-GLOC-0010": TIMEOUT, // timeout
	"OS-PLUG-GLOC-0011": POSITION_UNAVAILABLE, // timeout must be positive (Android)
	"OS-PLUG-GLOC-0012": POSITION_UNAVAILABLE, // watchId not found (Android)
	"OS-PLUG-GLOC-0013": POSITION_UNAVAILABLE, // watchId not provided (Android)
	"OS-PLUG-GLOC-0014": POSITION_UNAVAILABLE, // Google Play Services resolvable error (Android)
	"OS-PLUG-GLOC-0015": POSITION_UNAVAILABLE, // Google Play Services error (Android)
	"OS-PLUG-GLOC-0016": POSITION_UNAVAILABLE, // location settings error (Android)
	"OS-PLUG-GLOC-0017": PERMISSION_DENIED, // network and location both off (Android)
	"OS-PLUG-GLOC-0018": POSITION_UNAVAILABLE, // location permissions missing from manifest (Android)
};

type NativeGeolocationPositionError = GeolocationPositionError & {
	nativeCode: string | null;
};

function toGeolocationPositionError(
	err: unknown,
): NativeGeolocationPositionError {
	const nativeCode = readNativeCode(err);
	const code =
		nativeCode === null
			? POSITION_UNAVAILABLE
			: (NATIVE_CODE_TO_POSITION_ERROR_CODE[nativeCode] ??
				POSITION_UNAVAILABLE);
	return {
		code,
		message: readMessage(err),
		nativeCode,
		PERMISSION_DENIED,
		POSITION_UNAVAILABLE,
		TIMEOUT,
	};
}

function readNativeCode(err: unknown): string | null {
	if (typeof err === "object" && err !== null && "code" in err) {
		return typeof err.code === "string" ? err.code : null;
	}
	return null;
}

function readMessage(err: unknown): string {
	if (typeof err === "object" && err !== null && "message" in err) {
		return String(err.message);
	}
	return String(err);
}
